import { ConsoleAnalyticsAdapter, NoopAnalyticsAdapter, type AnalyticsAdapter } from './AnalyticsAdapter';
import { PlausibleAnalyticsAdapter } from './PlausibleAnalyticsAdapter';

export type AnalyticsProviderId = 'none' | 'console' | 'plausible';

export interface AnalyticsConfig {
  provider: AnalyticsProviderId;
  domain?: string;
  host?: string;
}

/** Read once at boot from the build environment. */
export function readAnalyticsConfig(): AnalyticsConfig {
  const provider = (import.meta.env.VITE_ANALYTICS_PROVIDER ?? 'none') as AnalyticsProviderId;
  return {
    provider,
    domain: import.meta.env.VITE_ANALYTICS_DOMAIN,
    host: import.meta.env.VITE_ANALYTICS_HOST,
  };
}

/**
 * Consent is the gate, not a flag on the event. Without it we return the no-op
 * adapter and no vendor script is ever loaded.
 */
export function createAnalytics(config: AnalyticsConfig, hasConsent: boolean): AnalyticsAdapter {
  if (!hasConsent) return new NoopAnalyticsAdapter();

  switch (config.provider) {
    case 'plausible':
      return config.domain
        ? new PlausibleAnalyticsAdapter(config.domain, config.host)
        : new NoopAnalyticsAdapter();
    case 'console':
      return new ConsoleAnalyticsAdapter();
    case 'none':
    default:
      return new NoopAnalyticsAdapter();
  }
}

export const isAnalyticsConfigured = (config: AnalyticsConfig): boolean =>
  config.provider !== 'none';
